"use client"

import * as Checkbox from "@radix-ui/react-checkbox"
import * as Form from "@radix-ui/react-form"
import * as VisuallyHidden from "@radix-ui/react-visually-hidden"

import Primary from "components/Buttons/Button"
import CheckL from "images/Icons/CheckL.inline.svg"
import XLSVG from "images/Icons/XL.inline.svg"
import colors from "styles/colors"
import textStyles from "styles/text"

import { MorphSVGPlugin } from "gsap/MorphSVGPlugin"
import gsap from "gsap/all"
import { css, f, styled } from "library/styled"
import { useAnimation } from "library/useAnimation"
import { useMedia } from "library/useMedia"
import { useRef, useState } from "react"
import links from "utils/links"
import LinkButton from "./Buttons/Link"

gsap.registerPlugin(MorphSVGPlugin)

const PORTAL_ID = "22153895"
const FORM_ID = "19e77a38-8615-440b-b5a0-b98c3ac12f48"

export default function ContactForm({
	onComplete,
	fullPage = false,
}: {
	onComplete?: () => void
	fullPage?: boolean
}) {
	const morphPathRef = useRef(null)
	const checkedPathRef = useRef(null)
	const formRef = useRef(null)
	const [success, setSuccess] = useState(false)
	const mobile = useMedia(false, false, false, true)

	useAnimation(() => {
		if (!formRef.current) return

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: formRef.current,
				start: "top 80%",
				end: "bottom top",
				scrub: true,
			},
		})

		tl.to(
			formRef.current,
			{
				y: 0,
				duration: 0.4,
			},
			0,
		)
		tl.to(
			formRef.current,
			{
				y: -24,
				duration: 0.4,
			},
			0.6,
		)
	}, [])

	return (
		<FormContainer>
			<FormRoot
				ref={formRef}
				$success={success}
				onSubmit={(e) => {
					e.preventDefault()
					const url = `https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${FORM_ID}`

					const data = new FormData(e.currentTarget)
					const submission = {
						fields: [...data.entries()].map(([name, value]) => ({
							name,
							value,
						})),
					}

					fetch(url, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify(submission),
					})
						.then(() => {
							if (onComplete) {
								onComplete()
								setSuccess(true)
							}
							return null
						})
						.catch((error) => {
							console.error(error)
						})
				}}
			>
				<Header fullPage={fullPage}>
					<Title>
						Ready for better coverage?
						{mobile && (
							<>
								&nbsp;Let's <GreenText>get you started.</GreenText>
							</>
						)}
					</Title>
					<SubTitle fullPage={fullPage}>
						Let's <GreenText>get you started.</GreenText>
					</SubTitle>
					{fullPage && (
						<FullPageText>
							<p>222 Broadway, #2424, New York, NY 10038 </p>
							<LinkButton variant="alt" size="alt" href={links.mailto}>
								quotes@joinArlo.com
							</LinkButton>
						</FullPageText>
					)}
				</Header>
				<TextFields>
					<FormField name="firstname" style={{ gridArea: "firstname" }}>
						<IconContainer>
							<CheckL className="input-valid" />
							<XLSVG className="input-invalid" />
						</IconContainer>
						<Form.Control asChild>
							<Input name="firstname" placeholder="First Name" required />
						</Form.Control>
						<FormMessage match="valueMissing">
							Please enter your first name
						</FormMessage>
						<VisuallyHidden.Root>
							<Label>First Name</Label>
						</VisuallyHidden.Root>
					</FormField>

					<FormField name="lastname" style={{ gridArea: "lastname" }}>
						<IconContainer>
							<CheckL className="input-valid" />
							<XLSVG className="input-invalid" />
						</IconContainer>
						<Form.Control asChild>
							<Input name="lastname" placeholder="Last Name" required />
						</Form.Control>
						<FormMessage match="valueMissing">
							Please enter your last name
						</FormMessage>
						<VisuallyHidden.Root>
							<Label>Last Name</Label>
						</VisuallyHidden.Root>
					</FormField>

					<FormField name="company" style={{ gridArea: "company" }}>
						<IconContainer>
							<CheckL className="input-valid" />
							<XLSVG className="input-invalid" />
						</IconContainer>
						<Form.Control asChild>
							<Input name="company" placeholder="Company" required />
						</Form.Control>
						<FormMessage match="valueMissing">
							Please enter your company
						</FormMessage>
						<VisuallyHidden.Root>
							<Label>Company</Label>
						</VisuallyHidden.Root>
					</FormField>

					<FormField name="email" style={{ gridArea: "email" }}>
						<IconContainer>
							<CheckL className="input-valid" />
							<XLSVG className="input-invalid" />
						</IconContainer>
						<Form.Control asChild>
							<Input name="email" placeholder="Email" required type="email" />
						</Form.Control>
						<FormMessage match="valueMissing">
							Please enter your email
						</FormMessage>
						<FormMessage match="typeMismatch">
							Please enter a valid email
						</FormMessage>
						<FormMessage match={(v) => !/@.*\./.test(v)}>
							Please enter a valid email
						</FormMessage>
						<VisuallyHidden.Root>
							<Label>Email</Label>
						</VisuallyHidden.Root>
					</FormField>

					<FormField name="phone" style={{ gridArea: "phone" }}>
						<IconContainer>
							<CheckL className="input-valid" />
							<XLSVG className="input-invalid" />
						</IconContainer>
						<Form.Control asChild>
							<Input
								name="phone"
								placeholder="Phone Number"
								required
								type="tel"
							/>
						</Form.Control>
						<FormMessage match="valueMissing">
							Please enter your phone number
						</FormMessage>

						<FormMessage
							match={(value) => {
								if (!value) return false
								return (
									!/^[0-9 +()-]+$/.test(value) ||
									value.replace(/\D/g, "").length < 10
								)
							}}
						>
							Please enter a valid phone number
						</FormMessage>
						<VisuallyHidden.Root>
							<Label>Phone Number</Label>
						</VisuallyHidden.Root>
					</FormField>
				</TextFields>
				<SubmitArea>
					<MessageField name="message">
						<IconContainer>
							<CheckL className="input-valid" />
							<XLSVG className="input-invalid" />
						</IconContainer>
						<VisuallyHidden.Root>
							<Form.Label>Write a message</Form.Label>
						</VisuallyHidden.Root>
						<Form.Control asChild>
							<TextArea name="message" placeholder="Write a message" required />
						</Form.Control>
						<FormMessage match="valueMissing">
							Please enter a message
						</FormMessage>
					</MessageField>
					<Row>
						<Form.Field name="newsletter">
							<CheckboxContainer>
								<Form.Control asChild>
									<CheckboxRoot name="newsletter" id="newsletter">
										<CheckboxIndicator forceMount>
											<svg
												viewBox="0 0 26 26"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<title>check</title>
												<path
													className="unchecked"
													ref={morphPathRef}
													d="M19.2475 2.75252C17.4277 1.6407 15.2887 1 13 1C6.37258 1 1 6.37258 1 13C1 19.6274 6.37258 25 13 25C19.6274 25 25 19.6274 25 13C25 11.1173 24.5664 9.33579 23.7937 7.75"
													stroke="#6E9E8F"
													strokeWidth="0.75"
													strokeLinecap="round"
													strokeLinejoin="round"
													fill="transparent"
												/>

												<path
													className="checked"
													ref={checkedPathRef}
													d="M25 13C25 19.6274 19.6274 25 13 25C6.37258 25 1 19.6274 1 13C1 6.37258 6.37258 1 13 1C15.2887 1 17.4277 1.6407 19.2475 2.75252M9.57143 13L13 16.4286L25 4.42857"
													stroke={colors.green300}
													strokeWidth="0.75"
													strokeLinecap="round"
													strokeLinejoin="round"
													fill="transparent"
												/>
											</svg>
										</CheckboxIndicator>
									</CheckboxRoot>
								</Form.Control>
								<CheckboxLabel htmlFor="newsletter">
									Receive Emails
								</CheckboxLabel>
							</CheckboxContainer>
						</Form.Field>

						<Form.Submit asChild>
							<SubmitButton aria-disabled={true} size="large" type="submit">
								Submit Form
							</SubmitButton>
						</Form.Submit>
					</Row>
				</SubmitArea>
			</FormRoot>

			<SuccessMessage $success={success}>
				<Header>
					<Title>
						<GreenText>Thank you</GreenText> for {mobile && "your submission."}
					</Title>
					<SubTitle>your submission.</SubTitle>
				</Header>
				<SubText>
					We got your message and will be sure to reach out as soon as possible.
				</SubText>
			</SuccessMessage>
		</FormContainer>
	)
}

const Input = styled("input", {
	...f.responsive(css`
		${textStyles.titleS};
		background: ${colors.white};
		color: ${colors.green500};
		caret-color: ${colors.green500};
		padding: 16px;
		border: 0.75px solid ${colors.green300};
		transition: 0.2s ease-in-out;
		transition-property: border-bottom, color;
		width: 100%;
		display: block;
		border-radius: 4px;

		&::placeholder {
			color: ${colors.green300};
			transition:
				color 0.2s ease,
				opacity 0.2s ease;
		}

		&:hover {
			border: 0.75px solid ${colors.green100};

			&::placeholder {
				color: ${colors.greenAccent};
			}
		}

		&:focus-visible {
			outline: none;
			border: 0.75px solid ${colors.green100};

			&::placeholder {
				color: transparent;
				opacity: 0;
			}
		}

		&[data-invalid] {
			color: ${colors.orange};
			border: 0.75px solid ${colors.orange};

			&::placeholder {
				color: ${colors.orange};
			}
		}

		&[data-valid] {
			border: 0.75px solid ${colors.green200};
		}
	`),
})

const TextArea = styled("textarea", {
	...f.responsive(css`
		${textStyles.titleS};
		padding: 15.5px 16px;
		color: ${colors.green500};
		caret-color: ${colors.green500};
		background-color: ${colors.white};
		border: 0.75px solid ${colors.green300};
		height: 250px;
		display: block;
		word-wrap: break-word;
		overflow-wrap: break-word;
		white-space: pre-wrap;
		width: 100%;
		box-sizing: border-box;
		border-radius: 4px;

		&::placeholder {
			color: ${colors.green300};
			transition:
				color 0.2s ease,
				opacity 0.2s ease;
		}

		&:focus {
			outline: none;
			border: 0.75px solid ${colors.green100};

			&::placeholder {
				color: transparent;
				opacity: 0;
			}
		}

		&[data-invalid] {
			color: ${colors.orange};
			border: 0.75px solid ${colors.orange};

			&::placeholder {
				color: ${colors.orange};
			}
		}

		&[data-valid] {
			border: 0.75px solid ${colors.green200};
		}
	`),
	...f.tablet(css`
		width: 100%;
		height: 371px;
	`),

	...f.mobile(css`
		width: 100%;
		height: 107px;
	`),
})

const FormContainer = styled("div", {
	...f.responsive(css`
		position: relative;
		width: 100%;
		margin: 0 auto;
		padding: 12px;
		border: 0.75px solid ${colors.green100};
		border-radius: 26px;
		overflow: clip;
	`),
	...f.tablet(css`
		width: 960px;
		padding: 24px;
	`),
	...f.mobile(css`
		width: 100%;
	`),
})

const FormRoot = styled(Form.Root, ({ $success }: { $success: boolean }) => ({
	...f.responsive(css`
		display: grid;
		grid-template:
			"top top" auto
			"fields submit" auto
			/ 538px auto;
		gap: 96px 64px;
		position: relative;
		width: 100%;
		margin: 0 auto;
		user-select: none;
		padding: 56px;
		border: 0.75px solid ${colors.green300};
		border-radius: 16px;
		transform: translateY(20px);
		opacity: ${$success ? 0 : 1};
		transition: opacity 0.4s ease-in-out;
	`),
	...f.tablet(css`
		grid-template:
			"top top" auto
			"fields fields" auto
			"submit submit" auto / 1fr 1fr;
		width: 100%;
		gap: 24px 0;
	`),
	...f.mobile(css`
		grid-template:
			"top" auto
			"fields" auto
			"submit" auto / 1fr;
		width: 100%;
		gap: 24px 0;
		padding: 20px;
	`),
}))

const Header = styled("div", ({ fullPage }: { fullPage?: boolean }) => ({
	...f.responsive(css`
		display: grid;
		grid-area: top;
		${fullPage
			? css`
					grid-template:
						"title1 title1" auto
						"space extra" 26px
						"subtitle extra" auto / 877px auto;
				`
			: css`
					display: flex;
					flex-direction: column;
					gap: 20px;
				`}
	`),

	...f.tablet(css`
		margin-bottom: 50px;
		${fullPage &&
		css`
			grid-template:
				"extra extra" auto
				"space space" 64px
				"title1 title1" auto
				". ." 24px
				"subtitle subtitle" auto
				/ 1fr 1fr;
		`}
	`),
	...f.mobile(css`
		margin-bottom: 24px;
		grid-template:
			"extra extra" auto
			"space space" 24px
			"title1 title1" auto
			". ." 24px
			"subtitle subtitle" auto
			/ 1fr 1fr;
	`),
}))

const Title = styled("h1", {
	...f.responsive(css`
		${textStyles.h3};
		color: ${colors.black100};
		text-align: left;
		grid-area: title1;
	`),
	...f.tablet(css`
		${textStyles.h4};
	`),
	...f.mobile(css`
		${textStyles.h6};
		text-align: center;
	`),
})

const SubTitle = styled("h1", ({ fullPage }: { fullPage?: boolean }) => ({
	...f.responsive(css`
		${textStyles.h3};
		color: ${colors.black100};
		text-align: ${fullPage ? "left" : "right"};
		grid-area: subtitle;
	`),
	...f.tablet(css`
		${textStyles.h4};
		text-align: right;
	`),
	...f.mobile(css`
		display: none;
	`),
}))

const GreenText = styled("span", {
	...f.responsive(css`
		display: inline-block;
		color: ${colors.green200};
		transform: skewX(-10deg);
	`),
})

const TextFields = styled("div", {
	...f.responsive(css`
		display: grid;
		grid-area: fields;
		gap: 16px;
		grid-template:
			"firstname" auto
			"lastname" auto
			"company" auto
			"email" auto
			"phone" auto / 1fr;
	`),
	...f.tablet(css`
		grid-template:
			"firstname phone" auto
			"lastname email" auto
			"company company" auto / 1fr 1fr;
		gap: 24px;
	`),
	...f.mobile(css`
		grid-template:
			"firstname" auto
			"lastname" auto
			"company" auto
			"phone" auto
			"email" auto / 1fr;
		gap: 24px;
	`),
})

const SubmitArea = styled("div", {
	...f.responsive(css`
		display: grid;
		grid-area: submit;
		gap: 24px;
	`),
})

const FormField = styled(Form.Field, {
	...f.responsive(css`
		position: relative;

		&:has(input[data-valid], textarea[data-valid]) .input-valid {
			opacity: 1;
		}

		&:has(input[data-invalid], textarea[data-invalid]) .input-invalid {
			opacity: 1;
		}
	`),
})

const MessageField = styled(Form.Field, {
	...f.responsive(css`
		position: relative;
		grid-column: 1 / -1;
	`),
})

const FormMessage = styled(Form.Message, {
	...f.responsive(css`
		${textStyles.titleXS};
		color: ${colors.orange};
		display: block;
		height: 0;
		translate: 10px 5px;
	`),
})

const Label = styled(Form.Label, {
	...f.responsive(css`
		pointer-events: none;
		position: absolute;
		right: 10px;
		top: 8px;
	`),
})

const IconContainer = styled("div", {
	...f.responsive(css`
		width: 24px;
		height: 24px;
		position: absolute;
		top: 24px;
		transform: translateY(-50%);
		right: 10px;
		z-index: 1;

		svg {
			position: absolute;
			width: 100%;
			height: 100%;
			opacity: 0;
		}

		.input-valid {
			color: ${colors.green200};
		}

		.input-invalid {
			color: ${colors.orange};
		}
	`),
	...f.mobile(css`
		top: 16px;
	`),
})

const CheckboxRoot = styled(Checkbox.Root, {
	...f.responsive(css`
		width: 24px;
		height: 24px;
		background-color: transparent;
		border: none;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		position: relative;
		padding: 0;
		margin: 0;

		&:focus {
			outline: none;
		}
	`),
})

const CheckboxIndicator = styled(Checkbox.Indicator, {
	...f.responsive(css`
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	`),
})

const CheckboxContainer = styled("div", {
	...f.responsive(css`
		position: relative;
		display: flex;
		align-items: center;
		gap: 8px;

		.checked,
		.unchecked {
			transition: opacity 0.3s ease-in-out;
		}

		.checked {
			opacity: 0;
		}

		[data-state="checked"] {
			.checked {
				opacity: 1;
			}

			.unchecked {
				opacity: 0;
			}
		}

		[data-state="unchecked"] {
			.checked {
				opacity: 0;
			}

			.unchecked {
				opacity: 1;
			}
		}
	`),
})

const CheckboxLabel = styled("label", {
	...f.responsive(css`
		${textStyles.titleS}
		color: ${colors.green300};
		cursor: pointer;
	`),
})

const SubmitButton = styled(Primary, {
	...f.responsive(css`
		place-self: end end;
	`),
})

const SuccessMessage = styled("div", ({ $success }: { $success: boolean }) => ({
	...f.responsive(css`
		position: absolute;
		display: grid;
		place-content: center;
		grid-template:
			"top top" auto
			"message message" auto
			/ 391px 391px;
		gap: 56px 0;
		width: calc(100% - 24px);
		height: calc(100% - 24px);
		user-select: none;
		padding: 56px;
		border: 0.75px solid ${colors.green300};
		border-radius: 16px;
		top: 12px;
		left: 12px;
		pointer-events: none;
		opacity: ${$success ? 1 : 0};
		transition: opacity 0.4s ease-in-out;
	`),
	...f.tablet(css`
		width: calc(100% - 48px);
		height: calc(100% - 48px);
		grid-template:
			"top top" auto
			"message message" auto
			/ 1fr 1fr;
		top: 24px;
		left: 24px;
		gap: 0;
	`),
	...f.mobile(css`
		grid-template:
			"top" auto
			"message" auto
			/ 1fr;
		top: 12px;
		left: 12px;
		gap: 0;
	`),
}))

const SubText = styled("p", {
	...f.responsive(css`
		${textStyles.bodyR};
		color: ${colors.green300};
		text-align: center;
		grid-area: message;
	`),

	...f.mobile(css`
		${textStyles.bodyS};
	`),
})

const Row = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
	`),

	...f.mobile(css`
		flex-direction: column;
		gap: 24px;
		align-items: flex-start;
	`),
})

const FullPageText = styled("div", {
	...f.responsive(css`
		${textStyles.titleS};
		grid-area: extra;
		text-align: right;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		justify-content: space-between;
		gap: 20px;
		color: ${colors.green300};

		p {
			width: 155px;
		}
	`),

	...f.tablet(css`
		${textStyles.titleS};
		display: grid;
		grid-template:
			"address mail" auto
			/ 1fr 1fr;

		p {
			grid-area: address;
			text-align: left;
		}

		a {
			grid-area: mail;
			justify-self: end;
		}
	`),
})
